import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Plus, Trash2, LogOut, Users as UsersIcon,
  Radio as RadioIcon, Share2, X, Copy, Settings, Search, Upload, Headphones,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SEGMENT_TYPES = ['editorial', 'news', 'interview', 'live-cross', 'feature', 'music', 'other'];
const STATUSES = ['planned', 'confirmed', 'recorded'];
const STATUS_COLOR = { planned: 'bg-gray-200 text-gray-700', confirmed: 'bg-green-100 text-green-700', recorded: 'bg-blue-100 text-blue-700' };

const pad = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; return addDays(x, -wd); };
const DAY_NAMES = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

// A guest counts as confirmed if explicitly ticked OR already invited (guests
// booked before per-guest confirmation existed have no flag but do have an invite).
const isConfirmed = (g) => Boolean(g?.confirmed || g?.inviteEventId);

// Booking state of a segment, for at-a-glance colour in the week/day views:
//   green  = guests allocated and all confirmed (booked)
//   amber  = guests allocated, still chasing at least one
//   ''     = no guests yet
const bookingTint = (it) => {
  const g = it.guests || [];
  if (!g.length) return '';
  return g.every(isConfirmed)
    ? 'bg-green-50 border-green-300'
    : 'bg-amber-50 border-amber-300';
};

const emptyItem = (date, showId) => ({
  date, showId: showId || '', time: '16:00', duration: '', title: '', host: '',
  guests: [], studioContact: '', notes: '', type: '', status: 'planned',
  publishToPalinsesto: false, podcast: false,
});

// Where an episode is up to. The audio is cut on a studio PC from the station
// logger, so "waiting" is normal until the programme has finished airing.
const PODCAST_STATE = {
  waiting:   { icon: '🎧', label: 'In attesa della fine della trasmissione' },
  pending:   { icon: '🎧⏳', label: 'In coda per il taglio audio' },
  cutting:   { icon: '🎧⏳', label: 'Taglio audio in corso' },
  uploaded:  { icon: '🎧⏳', label: 'Audio caricato, pubblicazione in corso' },
  published: { icon: '🎧✅', label: 'Pubblicato su Spotify (via Megaphone)' },
  draft:     { icon: '🎧📝', label: 'Bozza pronta su Megaphone — da approvare, NON su Spotify' },
  failed:    { icon: '🎧⚠️', label: 'Errore — vedi la scheda Podcast' },
  blocked:   { icon: '🎧⚙️', label: 'Nessun podcast Megaphone selezionato' },
  review:    { icon: '🎧⚠️', label: 'Cambio ora legale — da tagliare a mano' },
};
// A published-but-draft episode is NOT on Spotify — say so, or the tick lies.
const podcastState = (job) =>
  (job?.status === 'published' && job?.draft)
    ? PODCAST_STATE.draft
    : (PODCAST_STATE[job?.status] || PODCAST_STATE.waiting);

const AdminScaletta = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('adminToken');
  const role = sessionStorage.getItem('adminRole') || '';
  const username = sessionStorage.getItem('adminUser') || '';
  const myShows = useMemo(() => { try { return JSON.parse(sessionStorage.getItem('adminShows') || '[]'); } catch { return []; } }, []);
  const canManage = role === 'admin' || role === 'producer'; // shows + duplicate + share
  const isAdmin = role === 'admin';

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [view, setView] = useState('week');            // 'week' | 'day'
  const [selectedDate, setSelectedDate] = useState(() => fmt(new Date()));
  const [filterShow, setFilterShow] = useState('');
  const [filterHost, setFilterHost] = useState('');
  const [shows, setShows] = useState([]);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);        // segment modal
  const [showPanel, setShowPanel] = useState(null);    // 'shows' | 'users' | 'share' | null
  const [shareToken, setShareToken] = useState('');
  const [invitesReady, setInvitesReady] = useState(false);
  const [inviteMailbox, setInviteMailbox] = useState('');
  const [autoPublish, setAutoPublish] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [horizon, setHorizon] = useState(null);
  const [autoExtend, setAutoExtend] = useState(true);
  const [targetWeeks, setTargetWeeks] = useState(8);
  const [podcastJobs, setPodcastJobs] = useState([]);
  const [podcastCfg, setPodcastCfg] = useState({ configured: false, agentLastSeen: '', agentHost: '' });
  const [megaphoneShows, setMegaphoneShows] = useState([]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const showById = useMemo(() => Object.fromEntries(shows.map((s) => [s.id, s])), [shows]);

  // Hosts present in the loaded week (plus any show hosts), for the filter.
  const hostOptions = useMemo(() => {
    const set = new Set();
    items.forEach((i) => i.host && set.add(i.host));
    shows.forEach((s) => s.host && set.add(s.host));
    return [...set].sort();
  }, [items, shows]);

  const visibleItems = useMemo(() => items.filter(
    (i) => (!filterShow || i.showId === filterShow) && (!filterHost || i.host === filterHost)
  ), [items, filterShow, filterHost]);

  // A segment's own episode wins over the whole-show one it also belongs to.
  const jobBySegment = useMemo(() => {
    const m = {};
    podcastJobs.forEach((j) => (j.segmentIds || []).forEach((sid) => {
      if (j.kind === 'segment' || !m[sid]) m[sid] = j;
    }));
    return m;
  }, [podcastJobs]);

  const filtering = Boolean(filterShow || filterHost);

  // Day navigation — loads a different week automatically when crossing over.
  const goDay = (delta) => {
    const d = addDays(new Date(`${selectedDate}T12:00:00`), delta);
    setSelectedDate(fmt(d));
    const m = mondayOf(d);
    if (fmt(m) !== fmt(weekStart)) setWeekStart(m);
  };

  const dayItems = useMemo(
    () => visibleItems.filter((i) => i.date === selectedDate).sort((a, b) => (a.time || '').localeCompare(b.time || '')),
    [visibleItems, selectedDate]
  );

  const canEditShow = useCallback(
    (showId) => canManage || (role === 'host' && myShows.includes(showId)),
    [canManage, role, myShows]
  );

  const api = useCallback(async (endpoint, method = 'GET', body = null) => {
    const res = await fetch(`${BACKEND_URL}/api${endpoint}`, {
      method,
      headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : null,
    });
    if (res.status === 401) { sessionStorage.clear(); navigate('/admin'); return null; }
    return res;
  }, [token, navigate]);

  const flash = (m) => { setMessage(m); setTimeout(() => setMessage(''), 2500); };

  const loadWeek = useCallback(async () => {
    setLoading(true);
    try {
      const from = fmt(weekStart), to = fmt(addDays(weekStart, 6));
      const [showsRes, itemsRes] = await Promise.all([api('/admin/shows'), api(`/admin/rundown?from=${from}&to=${to}`)]);
      if (showsRes?.ok) setShows(await showsRes.json());
      if (itemsRes?.ok) setItems(await itemsRes.json());
      if (isAdmin) { const u = await api('/admin/users'); if (u?.ok) setUsers(await u.json()); }
      const inv = await api('/admin/invites/status');
      if (inv?.ok) { const s = await inv.json(); setInvitesReady(!!s.configured); setInviteMailbox(s.mailbox || ''); }
      const st = await api('/admin/scaletta-settings');
      if (st?.ok) {
        const s = await st.json();
        setAutoPublish(!!s.autoPublish);
        setAutoExtend(!!s.autoExtend);
        setTargetWeeks(s.targetWeeks || 8);
      }
      // Keep the rolling planning window topped up automatically.
      if (canManage) {
        const ax = await api('/admin/rundown/auto-extend', 'POST');
        if (ax?.ok) {
          const d = await ax.json();
          if (d.created > 0) flash(`Palinsesto esteso automaticamente: +${d.created} segmenti (${d.weeksAhead} settimane in avanti)`);
        }
      }
      const hz = await api('/admin/rundown/horizon');
      if (hz?.ok) setHorizon(await hz.json());
      // Podcast state for the week (also nudges the server to rebuild the queue,
      // so ticking 🎧 on an already-aired segment takes effect immediately).
      const pj = await api(`/admin/podcast/jobs?from=${from}&to=${to}`);
      if (pj?.ok) setPodcastJobs(await pj.json());
      const ps = await api('/admin/podcast/status');
      if (ps?.ok) {
        const cfg = await ps.json();
        setPodcastCfg(cfg);
        if (cfg.configured) {
          const mp = await api('/admin/podcast/podcasts');
          if (mp?.ok) setMegaphoneShows((await mp.json()).podcasts || []);
        }
      }
    } catch (e) { flash('Errore nel caricamento'); }
    finally { setLoading(false); }
  }, [api, weekStart, isAdmin, canManage]);

  useEffect(() => { if (!token) { navigate('/admin'); return; } loadWeek(); }, [token, navigate, loadWeek]);

  const logout = () => { sessionStorage.clear(); navigate('/admin'); };

  // ----- segment save/delete -----
  const saveSegment = async () => {
    if (!editing.title?.trim()) { flash('Titolo obbligatorio'); return; }
    const res = editing.id
      ? await api(`/admin/rundown/${editing.id}`, 'PUT', editing)
      : await api('/admin/rundown', 'POST', editing);
    if (!res?.ok) {
      if (res) flash(res.status === 403 ? 'Non hai i permessi per questo programma' : 'Errore nel salvataggio');
      return;
    }
    const savedId = editing.id || (await res.json()).id;

    // A guest marked "Confermato" with an email and no invite yet -> offer to
    // send. Confirmation is per GUEST, not per segment. Asked explicitly so a
    // mis-click never emails someone. ONE meeting per SLOT: every segment at the
    // same date+time shares the room, so the playout system gets all the feeds
    // from a single call.
    const pending = (editing.guests || []).filter((g) => g.email?.trim() && g.confirmed && !g.inviteEventId);
    if (pending.length && invitesReady) {
      const who = pending.map((g) => `${g.name || '(senza nome)'} <${g.email}>`).join('\n');
      const verb = editing.inviteEventId
        ? 'Aggiungere alla riunione Teams esistente'
        : 'Creare la riunione Teams e invitare';
      if (window.confirm(`${verb}:\n\n${who}\n\nUn'unica riunione per la fascia oraria — tutti gli ospiti delle ${editing.time} ricevono lo STESSO link. Da ${inviteMailbox}, il ${editing.date}.`)) {
        const ir = await api(`/admin/rundown/${savedId}/invite`, 'POST');
        if (ir?.ok) {
          const d = await ir.json();
          const shared = d.segments > 1 ? ` (link condiviso con ${d.segments} segmenti delle ${editing.time})` : '';
          flash(d.action === 'updated'
            ? `Aggiunti alla riunione: ${d.sent.join(', ')}${shared}`
            : `Invito inviato a ${d.sent.join(', ')}${shared}`);
        } else if (ir) {
          const msg = await ir.json().catch(() => ({}));
          flash(ir.status === 503 ? 'Integrazione Microsoft 365 non ancora configurata' : (msg.detail || 'Errore nell\'invio dell\'invito'));
        }
      }
    }
    setEditing(null); flash('Salvato'); loadWeek();
  };
  const deleteSegment = async () => {
    if (!editing.id || !window.confirm('Eliminare questo segmento?')) return;
    const res = await api(`/admin/rundown/${editing.id}`, 'DELETE');
    if (res?.ok) { setEditing(null); flash('Eliminato'); loadWeek(); }
  };

  const duplicateWeek = async () => {
    const target = mondayOf(addDays(weekStart, 7));
    if (!window.confirm(`Copiare tutta la settimana nella settimana del ${fmt(target)}?`)) return;
    const res = await api('/admin/rundown/duplicate-week', 'POST', { fromMonday: fmt(weekStart), toMonday: fmt(target) });
    if (res?.ok) { const d = await res.json(); flash(`Copiati ${d.copied} segmenti`); setWeekStart(target); }
  };

  const publishWeek = async () => {
    const flagged = items.filter((i) => i.publishToPalinsesto).length;
    if (!flagged) { flash('Nessun segmento marcato "Pubblica nel Palinsesto"'); return; }
    if (!window.confirm(`Pubblicare ${flagged} segmenti nel palinsesto pubblico?\n\nI giorni interessati verranno sostituiti con questi segmenti (solo ora, titolo e conduttore).`)) return;
    const res = await api('/admin/rundown/publish', 'POST', { fromMonday: fmt(weekStart) });
    if (res?.ok) { const d = await res.json(); flash(`Pubblicati ${d.items} segmenti (${d.daysUpdated.length} giorni)`); }
    else if (res) flash('Errore nella pubblicazione');
  };

  const extendForward = async () => {
    const weeksStr = window.prompt(
      'Per quante settimane vuoi estendere questa struttura in avanti?\n\n' +
      'Gli ospiti NON vengono copiati (solo la struttura dei segmenti), e le settimane già pianificate non vengono toccate.',
      '8'
    );
    if (!weeksStr) return;
    const weeks = parseInt(weeksStr, 10);
    if (!weeks || weeks < 1) { flash('Numero di settimane non valido'); return; }
    setLoading(true);
    const res = await api('/admin/rundown/extend', 'POST', { fromMonday: fmt(weekStart), weeks, includeGuests: false });
    setLoading(false);
    if (res?.ok) {
      const d = await res.json();
      flash(`${d.created} segmenti creati su ${d.weeksFilled.length} settimane` + (d.weeksSkipped.length ? ` (${d.weeksSkipped.length} già pianificate, saltate)` : ''));
      loadHorizon();
    } else if (res) {
      const e = await res.json().catch(() => ({}));
      flash(e.detail || 'Errore nell\'estensione');
    }
  };

  const loadHorizon = useCallback(async () => {
    const r = await api('/admin/rundown/horizon');
    if (r?.ok) setHorizon(await r.json());
  }, [api]);

  const saveSettings = async (patch) => {
    const next = { autoPublish, autoExtend, targetWeeks, ...patch };
    const r = await api('/admin/scaletta-settings', 'PUT', next);
    if (r?.ok) {
      setAutoPublish(next.autoPublish);
      setAutoExtend(next.autoExtend);
      setTargetWeeks(next.targetWeeks);
      flash('Impostazioni salvate');
    }
  };

  // Jump to a segment's day from search results.
  const openDate = (dateStr) => {
    setSelectedDate(dateStr);
    setWeekStart(mondayOf(new Date(`${dateStr}T12:00:00`)));
    setView('day');
    setSearchOpen(false);
  };

  const openShare = async () => {
    const res = await api('/admin/share-link');
    if (res?.ok) { setShareToken((await res.json()).token); setShowPanel('share'); }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/rete-italia-logo.png" alt="Rete Italia" className="h-9" />
            <h1 className="text-lg font-bold text-gray-900">Scaletta</h1>
            <span className="text-xs text-gray-500 hidden sm:inline">{username} · {role}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setSearchOpen(true)} variant="outline" size="sm" title="Cerca in tutte le settimane"><Search className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Cerca</span></Button>
            {canManage && <Button onClick={() => setShowPanel('shows')} variant="outline" size="sm"><RadioIcon className="w-4 h-4 mr-1" />Programmi</Button>}
            {canManage && (
              <Button onClick={() => setShowPanel('podcast')} variant="outline" size="sm" title="Episodi podcast">
                <Headphones className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Podcast</span>
                {podcastJobs.some((j) => j.status === 'failed') && <span className="ml-1 text-red-500">⚠️</span>}
              </Button>
            )}
            {isAdmin && <Button onClick={() => setShowPanel('users')} variant="outline" size="sm"><UsersIcon className="w-4 h-4 mr-1" />Utenti</Button>}
            {canManage && <Button onClick={openShare} variant="outline" size="sm"><Share2 className="w-4 h-4 mr-1" />Condividi</Button>}
            {isAdmin && <Link to="/admin/dashboard" className="text-sm text-gray-600 hover:text-[#009246] px-2 hidden md:block">Admin</Link>}
            <Button onClick={logout} variant="outline" size="sm"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      {message && <div className="fixed top-16 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm">{message}</div>}

      <div className="container mx-auto px-4 py-4">
        {/* Navigation + view toggle */}
        <div className="flex items-center justify-between mb-3 bg-white rounded-xl shadow p-3 gap-2">
          <Button onClick={() => (view === 'week' ? setWeekStart(mondayOf(addDays(weekStart, -7))) : goDay(-1))} variant="ghost" size="icon"><ChevronLeft /></Button>
          <div className="text-center min-w-0">
            <div className="font-semibold truncate">
              {view === 'week'
                ? `Settimana del ${weekStart.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : new Date(`${selectedDate}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <button
              onClick={() => { const t = new Date(); setWeekStart(mondayOf(t)); setSelectedDate(fmt(t)); }}
              className="text-xs text-[#009246] hover:underline"
            >Oggi</button>
          </div>
          <div className="flex items-center gap-2">
            {canManage && <Button onClick={publishWeek} variant="outline" size="sm" title="Pubblica i segmenti marcati nel palinsesto pubblico">📅 Pubblica</Button>}
            {canManage && <Button onClick={extendForward} variant="outline" size="sm" title="Estendi questa struttura in avanti (senza ospiti)" className="hidden md:inline-flex">Estendi →</Button>}
            {canManage && <Button onClick={duplicateWeek} variant="outline" size="sm" title="Copia questa settimana nella prossima" className="hidden xl:inline-flex">Duplica →</Button>}
            <Button onClick={() => (view === 'week' ? setWeekStart(mondayOf(addDays(weekStart, 7))) : goDay(1))} variant="ghost" size="icon"><ChevronRight /></Button>
          </div>
        </div>

        {/* Forward-planning horizon */}
        {canManage && horizon && horizon.weeksAhead < targetWeeks && !autoExtend && (
          <div className="mb-3 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm">
            <span className="text-amber-900">
              📅 Palinsesto pianificato solo <b>{horizon.weeksAhead} settiman{horizon.weeksAhead === 1 ? 'a' : 'e'}</b> in avanti
              {horizon.lastPlanned && <span className="text-amber-700"> (fino al {new Date(`${horizon.lastPlanned}T12:00:00`).toLocaleDateString('it-IT')})</span>}
              . Per prenotare gli ospiti in anticipo servono almeno {targetWeeks} settimane.
            </span>
            <Button onClick={extendForward} size="sm" className="bg-amber-600 hover:bg-amber-700 whitespace-nowrap">Estendi ora</Button>
          </div>
        )}

        {/* View toggle + filters */}
        <div className="flex items-center flex-wrap gap-2 mb-4 bg-white rounded-xl shadow p-3">
          <div className="flex rounded-lg border overflow-hidden">
            {['week', 'day'].map((v) => (
              <button
                key={v}
                onClick={() => { setView(v); if (v === 'day' && !weekDays.some((d) => fmt(d) === selectedDate)) setSelectedDate(fmt(weekStart)); }}
                className={`px-3 py-1.5 text-sm font-medium ${view === v ? 'bg-[#009246] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >{v === 'week' ? 'Settimana' : 'Giorno'}</button>
            ))}
          </div>

          <select value={filterShow} onChange={(e) => setFilterShow(e.target.value)} className="px-2 py-1.5 border rounded-lg text-sm">
            <option value="">Tutti i programmi</option>
            {shows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <select value={filterHost} onChange={(e) => setFilterHost(e.target.value)} className="px-2 py-1.5 border rounded-lg text-sm">
            <option value="">Tutti i conduttori</option>
            {hostOptions.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>

          {filtering && (
            <button onClick={() => { setFilterShow(''); setFilterHost(''); }} className="text-sm text-[#009246] hover:underline flex items-center gap-1">
              <X className="w-3 h-3" />Azzera filtri
            </button>
          )}
          <span className="hidden lg:flex items-center gap-3 text-[11px] text-gray-500 pl-2">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-green-300 bg-green-50 inline-block" />ospiti confermati</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-amber-300 bg-amber-50 inline-block" />da confermare</span>
          </span>
          <div className="ml-auto flex items-center gap-3">
            {canManage && (
              <>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" title="Aggiorna automaticamente il palinsesto pubblico quando modifichi un segmento marcato, nella settimana corrente">
                  <input type="checkbox" checked={autoPublish} onChange={() => saveSettings({ autoPublish: !autoPublish })} />
                  Auto-pubblica
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" title={`Mantiene sempre almeno ${targetWeeks} settimane pianificate in avanti, copiando l'ultima settimana (senza ospiti)`}>
                  <input type="checkbox" checked={autoExtend} onChange={() => saveSettings({ autoExtend: !autoExtend })} />
                  Auto-estendi
                  <select
                    value={targetWeeks}
                    onChange={(e) => saveSettings({ targetWeeks: parseInt(e.target.value, 10) })}
                    onClick={(e) => e.stopPropagation()}
                    className="border rounded px-1 py-0.5 text-xs"
                    disabled={!autoExtend}
                  >
                    {[4, 6, 8, 10, 12, 16].map((w) => <option key={w} value={w}>{w} sett.</option>)}
                  </select>
                </label>
              </>
            )}
            <span className="text-xs text-gray-500">
              {view === 'day' ? `${dayItems.length} segmenti` : `${visibleItems.length} segmenti nella settimana`}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#009246]" /></div>
        ) : view === 'week' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
            {weekDays.map((day, i) => {
              const dateStr = fmt(day);
              const cellItems = visibleItems.filter((it) => it.date === dateStr).sort((a, b) => (a.time || '').localeCompare(b.time || ''));
              return (
                <div key={dateStr} className="bg-white rounded-xl shadow-sm flex flex-col min-h-[120px]">
                  <div className="px-3 py-2 border-b flex items-center justify-between">
                    <div>
                      <button onClick={() => { setSelectedDate(dateStr); setView('day'); }} className="font-semibold text-sm hover:text-[#009246] hover:underline text-left" title="Apri la vista giorno">{DAY_NAMES[i]}</button>
                      <div className="text-xs text-gray-500">{day.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</div>
                    </div>
                    <button onClick={() => setEditing(emptyItem(dateStr, canManage ? '' : myShows[0]))} className="p-1 text-[#009246] hover:bg-green-50 rounded" title="Aggiungi segmento"><Plus className="w-4 h-4" /></button>
                  </div>
                  <div className="p-2 space-y-2 flex-1">
                    {cellItems.length === 0 && <p className="text-xs text-gray-400 text-center py-4">—</p>}
                    {cellItems.map((it) => {
                      const s = showById[it.showId];
                      const editable = it._canEdit ?? canEditShow(it.showId);
                      return (
                        <button
                          key={it.id}
                          onClick={() => setEditing({ ...it, guests: it.guests || [] })}
                          className={`w-full text-left rounded-lg border p-2 hover:shadow transition ${bookingTint(it)} ${editable ? '' : 'opacity-70'}`}
                          style={{ borderLeft: `4px solid ${s?.color || '#9ca3af'}` }}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-xs font-semibold text-gray-700">{it.time}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_COLOR[it.status] || STATUS_COLOR.planned}`}>{it.status}</span>
                          </div>
                          <div className="text-sm font-medium truncate">{it.title}</div>
                          {s && <div className="text-[11px] text-gray-500 truncate">{s.name}</div>}
                          {it.host && <div className="text-[11px] text-gray-500 truncate">🎙 {it.host}</div>}
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-400">
                            {it.guests?.length > 0 && (
                              <span title="ospiti confermati / totali">
                                👤 {it.guests.filter(isConfirmed).length}/{it.guests.length}
                              </span>
                            )}
                            {it.publishToPalinsesto && <span title="Pubblicato nel palinsesto">📅</span>}
                            {it.podcast && <span title={podcastState(jobBySegment[it.id]).label}>{podcastState(jobBySegment[it.id]).icon}</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ---------- DAY VIEW: segments flow left-to-right across columns ---------- */
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-700">
                {new Date(`${selectedDate}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h2>
              <Button onClick={() => setEditing(emptyItem(selectedDate, canManage ? '' : myShows[0]))} className="bg-[#009246]" size="sm">
                <Plus className="w-4 h-4 mr-1" />Segmento
              </Button>
            </div>
            {dayItems.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">
                Nessun segmento{filtering ? ' con questi filtri' : ''} per questo giorno.
              </div>
            ) : (
              <>
              {/* Mobile: stacked cards (the table is too wide for phones) */}
              <div className="md:hidden space-y-2">
                {dayItems.map((it) => {
                  const s = showById[it.showId];
                  const editable = it._canEdit ?? canEditShow(it.showId);
                  return (
                    <button
                      key={it.id}
                      onClick={() => setEditing({ ...it, guests: it.guests || [], links: it.links || [] })}
                      className={`w-full text-left rounded-xl shadow-sm p-3 border ${bookingTint(it) || 'bg-white'} ${editable ? '' : 'opacity-70'}`}
                      style={{ borderLeft: `4px solid ${s?.color || '#9ca3af'}` }}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-lg font-bold tabular-nums">{it.time}</span>
                        <span className="text-xs text-gray-400">{it.duration}</span>
                        <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_COLOR[it.status] || STATUS_COLOR.planned}`}>{it.status}</span>
                      </div>
                      <div className="font-medium mt-0.5">{it.title}</div>
                      <div className="text-xs text-gray-500">{s?.name}{s?.name && it.host ? ' · ' : ''}{it.host && `🎙 ${it.host}`}</div>
                      {it.guests?.length > 0 && (
                        <div className="mt-2 pt-2 border-t text-xs text-gray-600 space-y-0.5">
                          {it.guests.map((g, gi) => (
                            <div key={gi}>
                              👤 {g.name || '(senza nome)'}{g.role ? ` — ${g.role}` : ''}
                              {g.phone && <a href={`tel:${g.phone}`} onClick={(e) => e.stopPropagation()} className="ml-1 text-[#009246] underline">{g.phone}</a>}
                              {it._contactsHidden && <span className="ml-1 text-gray-400" title="Riservati al conduttore del programma">🔒</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {(it.links?.length > 0 || it.inviteJoinUrl || it.publishToPalinsesto || it.podcast) && (
                        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-gray-400">
                          {it.links?.length > 0 && <span>📎 {it.links.length}</span>}
                          {it.inviteJoinUrl && <span>✅ Teams</span>}
                          {it.publishToPalinsesto && <span>📅</span>}
                          {it.podcast && <span title={podcastState(jobBySegment[it.id]).label}>{podcastState(jobBySegment[it.id]).icon}</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Desktop: Outlook-diary / spreadsheet style, one row per segment */}
              <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="px-3 py-2 font-semibold w-20">Ora</th>
                      <th className="px-3 py-2 font-semibold w-20">Durata</th>
                      <th className="px-3 py-2 font-semibold">Programma</th>
                      <th className="px-3 py-2 font-semibold">Segmento</th>
                      <th className="px-3 py-2 font-semibold">Conduttore</th>
                      <th className="px-3 py-2 font-semibold">Ospiti</th>
                      <th className="px-3 py-2 font-semibold">Contatti</th>
                      <th className="px-3 py-2 font-semibold w-24">Stato</th>
                      <th className="px-3 py-2 font-semibold w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {dayItems.map((it) => {
                      const s = showById[it.showId];
                      const editable = it._canEdit ?? canEditShow(it.showId);
                      return (
                        <tr
                          key={it.id}
                          onClick={() => setEditing({ ...it, guests: it.guests || [], links: it.links || [] })}
                          className={`cursor-pointer hover:bg-gray-50 ${bookingTint(it)} ${editable ? '' : 'opacity-70'}`}
                        >
                          <td className="px-3 py-2 font-bold tabular-nums whitespace-nowrap border-l-4" style={{ borderLeftColor: s?.color || '#e5e7eb' }}>{it.time}</td>
                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{it.duration}</td>
                          <td className="px-3 py-2 text-gray-700">{s?.name || '—'}</td>
                          <td className="px-3 py-2 font-medium">
                            {it.title}
                            {it.type && <span className="ml-2 text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{it.type}</span>}
                            {it.notes && <div className="text-xs text-gray-400 font-normal line-clamp-1">{it.notes}</div>}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{it.host || '—'}</td>
                          <td className="px-3 py-2">
                            {it.guests?.length
                              ? it.guests.map((g, gi) => (
                                  <div key={gi} className="whitespace-nowrap">
                                    <span title={isConfirmed(g) ? 'Confermato' : 'Da confermare'}>{isConfirmed(g) ? '✅' : '⏳'}</span>{' '}
                                    {g.regular && <span title="Ospite fisso — ogni settimana">🔁</span>}{g.regular ? ' ' : ''}
                                    {g.name || '(senza nome)'}
                                    {g.role && <span className="text-gray-400"> — {g.role}</span>}
                                  </div>
                                ))
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            {it._contactsHidden && it.guests?.length > 0
                              ? <span className="text-gray-400" title="Riservati al conduttore del programma">🔒 riservati</span>
                              : it.guests?.filter((g) => g.phone || g.email).map((g, gi) => (
                                  <div key={gi} className="whitespace-nowrap">{g.phone}{g.phone && g.email ? ' · ' : ''}{g.email}</div>
                                ))}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_COLOR[it.status] || STATUS_COLOR.planned}`}>{it.status}</span>
                          </td>
                          <td className="px-3 py-2 text-[10px] text-gray-400 whitespace-nowrap">
                            {it.links?.length > 0 && <span title={`${it.links.length} allegati`}>📎{it.links.length} </span>}
                            {it.inviteJoinUrl && <span title="Riunione Teams creata">✅</span>}
                            {it.publishToPalinsesto && <span title="Pubblicato nel palinsesto"> 📅</span>}
                            {it.podcast && <span title={podcastState(jobBySegment[it.id]).label}> {podcastState(jobBySegment[it.id]).icon}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        )}
      </div>

      {editing && (
        <SegmentModal
          item={editing} setItem={setEditing} shows={shows} canManage={canManage}
          myShows={myShows} onSave={saveSegment} onDelete={deleteSegment} onClose={() => setEditing(null)}
          api={api} token={token} flash={flash} invitesReady={invitesReady}
          megaphoneShows={megaphoneShows} podcastCfg={podcastCfg}
          podcastJob={editing.id ? jobBySegment[editing.id] : null} reload={loadWeek}
          readOnly={!!editing.id && !(editing._canEdit ?? canEditShow(editing.showId))}
        />
      )}
      {searchOpen && <SearchModal api={api} shows={shows} onOpenDate={openDate} onClose={() => setSearchOpen(false)} />}
      {showPanel === 'podcast' && (
        <PodcastModal
          jobs={podcastJobs} cfg={podcastCfg} api={api} flash={flash} reload={loadWeek}
          onOpenDate={openDate} onClose={() => setShowPanel(null)}
        />
      )}
      {showPanel === 'shows' && (
        <ShowsModal
          shows={shows} api={api} onClose={() => setShowPanel(null)} reload={loadWeek} flash={flash}
          megaphoneShows={megaphoneShows} podcastCfg={podcastCfg}
        />
      )}
      {showPanel === 'users' && <UsersModal users={users} shows={shows} api={api} onClose={() => setShowPanel(null)} reload={loadWeek} flash={flash} />}
      {showPanel === 'share' && <ShareModal token={shareToken} api={api} setToken={setShareToken} onClose={() => setShowPanel(null)} flash={flash} />}
    </div>
  );
};

// ---------- Segment modal ----------
const SegmentModal = ({ item, setItem, shows, canManage, myShows, onSave, onDelete, onClose, api, token, flash, invitesReady, megaphoneShows, podcastCfg, podcastJob, reload, readOnly = false }) => {
  // The server blanks the contact fields for segments that are not yours and
  // sets _contactsHidden. Trust that flag over anything computed here - it is
  // the side that actually withheld the data.
  const contactsHidden = item._contactsHidden ?? readOnly;
  const set = (k, v) => setItem({ ...item, [k]: v });
  const [history, setHistory] = useState(null);
  const [uploading, setUploading] = useState(false);

  const links = item.links || [];
  const setLink = (idx, k, v) => { const l = [...links]; l[idx] = { ...l[idx], [k]: v }; set('links', l); };
  const addLink = () => set('links', [...links, { label: '', url: '', kind: 'link' }]);
  const removeLink = (idx) => set('links', links.filter((_, i) => i !== idx));

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/admin/upload`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd,
      });
      if (res.ok) {
        const d = await res.json();
        set('links', [...links, { label: file.name, url: d.url, kind: 'file' }]);
      } else {
        const err = await res.json().catch(() => ({}));
        flash?.(err.detail || 'Caricamento fallito');
      }
    } catch { flash?.('Caricamento fallito'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const loadHistory = async () => {
    if (!item.id) return;
    const r = await api(`/admin/rundown/${item.id}/history`);
    if (r?.ok) setHistory(await r.json());
  };
  // The segment's OWN programme always stays in the list. Filtering it out left
  // the select with no matching option, so another host's segment reported
  // "nessun programma" instead of the show it actually belongs to.
  const editableShows = canManage
    ? shows
    : shows.filter((s) => myShows.includes(s.id) || s.id === item.showId);
  const setGuest = (idx, k, v) => { const g = [...item.guests]; g[idx] = { ...g[idx], [k]: v }; set('guests', g); };
  const addGuest = () => set('guests', [...(item.guests || []), { name: '', role: '', phone: '', email: '', notes: '' }]);
  const removeGuest = (idx) => set('guests', item.guests.filter((_, i) => i !== idx));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-bold">{item.id ? 'Modifica segmento' : 'Nuovo segmento'} · {item.date}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        {readOnly && (
          <div className="mx-4 mt-4 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">
            Sola lettura — questo segmento è di un altro programma. Puoi vedere chi è ospite, non come contattarlo.
          </div>
        )}
        <fieldset disabled={readOnly} className="p-4 space-y-4 min-w-0">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-sm font-medium">Ora</span>
              <input type="time" value={item.time} onChange={(e) => set('time', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg" /></label>
            <label className="block"><span className="text-sm font-medium">Durata</span>
              <input value={item.duration} onChange={(e) => set('duration', e.target.value)} placeholder="es. 5 min" className="w-full mt-1 px-3 py-2 border rounded-lg" /></label>
          </div>
          <label className="block"><span className="text-sm font-medium">Programma</span>
            <select value={item.showId} onChange={(e) => set('showId', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg">
              <option value="">— nessun programma —</option>
              {editableShows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></label>
          <label className="block"><span className="text-sm font-medium">Titolo segmento *</span>
            <input value={item.title} onChange={(e) => set('title', e.target.value)} placeholder="es. Story of the Day" className="w-full mt-1 px-3 py-2 border rounded-lg" /></label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block col-span-1"><span className="text-sm font-medium">Conduttore</span>
              <input value={item.host} onChange={(e) => set('host', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg" /></label>
            <label className="block"><span className="text-sm font-medium">Tipo</span>
              <select value={item.type} onChange={(e) => set('type', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg">
                <option value="">—</option>{SEGMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></label>
            <label className="block"><span className="text-sm font-medium">Stato</span>
              <select value={item.status} onChange={(e) => set('status', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></label>
          </div>

          {/* Guests */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Ospiti e contatti</span>
              <Button onClick={addGuest} variant="outline" size="sm"><Plus className="w-4 h-4 mr-1" />Ospite</Button>
            </div>
            {item.inviteJoinUrl && (
              <div className="mb-2 text-xs bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                <span className="text-green-800">✅ Riunione Teams creata — tutti gli ospiti delle {item.time || 'stessa fascia'} condividono questa stanza</span>
                <a href={item.inviteJoinUrl} target="_blank" rel="noopener noreferrer" className="underline text-green-800 whitespace-nowrap">Apri Teams</a>
              </div>
            )}
            <div className="space-y-2">
              {(item.guests || []).map((g, idx) => (
                <div key={idx} className={`border rounded-lg p-2 ${isConfirmed(g) ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
                  {/* Booking is confirmed per guest — this is what sends the invite */}
                  <label className="flex items-center gap-2 mb-2 text-sm font-medium cursor-pointer flex-wrap">
                    <input type="checkbox" checked={isConfirmed(g)} onChange={(e) => setGuest(idx, 'confirmed', e.target.checked)} />
                    <span className={isConfirmed(g) ? 'text-green-800' : 'text-gray-600'}>
                      {isConfirmed(g) ? `Confermato${g.name ? ` — ${g.name}` : ''}` : `Da confermare${g.name ? ` — ${g.name}` : ''}`}
                    </span>
                    {isConfirmed(g) && !g.inviteEventId && g.email?.trim() && invitesReady && (
                      <span className="text-[10px] text-green-700 font-normal">— invito al salvataggio</span>
                    )}
                    {g.inviteSentAt && (
                      <span className="text-[10px] text-gray-500 font-normal">
                        · invito inviato {new Date(g.inviteSentAt).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </label>
                  {/* Ospite fisso: re-books and re-invites this guest one week at a
                      time, for ever, until it is unticked. Deleting a single week's
                      booking skips just that week — the guest returns the week after. */}
                  <label className="flex items-center gap-2 mb-2 text-xs cursor-pointer flex-wrap"
                         title="Riprenota e re-invita questo ospite ogni settimana, una settimana alla volta, finché non togli la spunta. Cancellare la prenotazione di una singola settimana salta solo quella settimana.">
                    <input type="checkbox" checked={Boolean(g.regular)} onChange={(e) => setGuest(idx, 'regular', e.target.checked)} />
                    <span className={g.regular ? 'text-purple-800 font-medium' : 'text-gray-600'}>
                      🔁 Ospite fisso — ogni settimana
                    </span>
                    {g.regular && (
                      <span className="text-[10px] text-purple-700 font-normal">
                        — riprenotato e invitato automaticamente per la settimana prossima
                      </span>
                    )}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={g.name} onChange={(e) => setGuest(idx, 'name', e.target.value)} placeholder="Nome ospite" className="px-2 py-1.5 border rounded" />
                    <input value={g.role} onChange={(e) => setGuest(idx, 'role', e.target.value)} placeholder="Ruolo (es. giornalista)" className="px-2 py-1.5 border rounded" />
                    {contactsHidden ? (
                      <div className="col-span-2 text-xs text-gray-500 bg-gray-100 border border-dashed border-gray-300 rounded px-2 py-1.5">
                        🔒 Telefono, email e note sono riservati al conduttore di questo programma
                      </div>
                    ) : (
                      <>
                        <input value={g.phone} onChange={(e) => setGuest(idx, 'phone', e.target.value)} placeholder="Telefono" className="px-2 py-1.5 border rounded" />
                        <input value={g.email} onChange={(e) => setGuest(idx, 'email', e.target.value)} placeholder="Email" className="px-2 py-1.5 border rounded" />
                        <input value={g.notes} onChange={(e) => setGuest(idx, 'notes', e.target.value)} placeholder="Note (studio / cross / ecc.)" className="px-2 py-1.5 border rounded col-span-2" />
                      </>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <button onClick={() => removeGuest(idx)} className="text-xs text-red-500 hover:underline">Rimuovi ospite</button>
                    {g.inviteEventId ? (
                      <span className="text-xs text-green-700">✅ Invito Teams inviato</span>
                    ) : contactsHidden ? null : !g.email?.trim() ? (
                      <span className="text-xs text-gray-400">Aggiungi un'email per poter invitare</span>
                    ) : !isConfirmed(g) ? (
                      <span className="text-xs text-gray-400">Spunta "Confermato" per invitare</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {contactsHidden ? (
            <div className="text-xs text-gray-500 bg-gray-100 border border-dashed border-gray-300 rounded px-2 py-1.5">
              🔒 Il contatto studio / regia è riservato al conduttore di questo programma
            </div>
          ) : (
            <label className="block"><span className="text-sm font-medium">Contatto studio / regia</span>
              <input value={item.studioContact} onChange={(e) => set('studioContact', e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg" /></label>
          )}
          <label className="block"><span className="text-sm font-medium">Note di produzione</span>
            <textarea value={item.notes} onChange={(e) => set('notes', e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 border rounded-lg" /></label>

          {/* Attachments & links */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Allegati e link</span>
              <div className="flex gap-2">
                <Button onClick={addLink} variant="outline" size="sm"><Plus className="w-4 h-4 mr-1" />Link</Button>
                <label className="inline-flex">
                  <span className={`inline-flex items-center px-3 py-1.5 text-sm border rounded-md cursor-pointer hover:bg-gray-50 ${uploading ? 'opacity-50' : ''}`}>
                    <Upload className="w-4 h-4 mr-1" />{uploading ? 'Carico…' : 'File'}
                  </span>
                  <input type="file" className="hidden" onChange={uploadFile} disabled={uploading}
                         accept=".pdf,.doc,.docx,.txt,.md,.rtf,.csv,.xlsx,.mp3,.m4a,.wav,.ogg,image/*" />
                </label>
              </div>
            </div>
            {links.length === 0 && <p className="text-xs text-gray-400">Note di preparazione, elenco domande, audio…</p>}
            <div className="space-y-2">
              {links.map((l, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">{l.kind === 'file' ? '📎' : '🔗'}</span>
                  <input value={l.label} onChange={(e) => setLink(idx, 'label', e.target.value)} placeholder="Etichetta" className="w-40 px-2 py-1.5 border rounded text-sm" />
                  <input value={l.url} onChange={(e) => setLink(idx, 'url', e.target.value)} placeholder="https://…" className="flex-1 px-2 py-1.5 border rounded text-sm" />
                  {l.url && <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#009246] underline whitespace-nowrap">apri</a>}
                  <button onClick={() => removeLink(idx)} className="p-1 text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!item.publishToPalinsesto} onChange={(e) => set('publishToPalinsesto', e.target.checked)} />Pubblica nel Palinsesto</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!item.podcast} onChange={(e) => set('podcast', e.target.checked)} />Segna per Podcast</label>
          </div>

          {item.podcast && (
            <PodcastPanel
              item={item} set={set} shows={shows} megaphoneShows={megaphoneShows}
              cfg={podcastCfg} job={podcastJob} api={api} flash={flash} reload={reload}
            />
          )}

        </fieldset>
        <div className="px-4 pb-4">
          {/* Change history */}
          {item.id && (
            <div className="border-t pt-3">
              {history === null ? (
                <button onClick={loadHistory} className="text-xs text-gray-500 hover:text-[#009246] hover:underline">
                  🕘 Mostra cronologia modifiche{item.updatedBy ? ` — ultima: ${item.updatedBy}` : ''}
                </button>
              ) : (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cronologia</div>
                  {history.length === 0 && <p className="text-xs text-gray-400">Nessuna modifica registrata.</p>}
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {history.map((h, i) => (
                      <div key={i} className="text-xs text-gray-600">
                        <span className="font-medium">{h.user}</span>
                        <span className="text-gray-400"> · {h.action} · {new Date(h.at).toLocaleString('it-IT')}</span>
                        {h.changes?.length > 0 && (
                          <ul className="ml-4 list-disc text-gray-500">
                            {h.changes.map((c, ci) => (
                              <li key={ci}>{c.field}: <span className="line-through opacity-60">{String(c.from ?? '—')}</span> → {String(c.to ?? '—')}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between p-4 border-t sticky bottom-0 bg-white">
          {item.id && !readOnly ? <Button onClick={onDelete} variant="outline" className="text-red-600"><Trash2 className="w-4 h-4 mr-1" />Elimina</Button> : <span />}
          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline">{readOnly ? 'Chiudi' : 'Annulla'}</Button>
            {!readOnly && <Button onClick={onSave} className="bg-[#009246]">Salva</Button>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Podcast controls inside the segment modal ----------
const PodcastPanel = ({ item, set, shows, megaphoneShows, cfg, job, api, flash, reload }) => {
  const show = shows.find((s) => s.id === item.showId);
  const inherited = megaphoneShows.find((p) => String(p.id) === String(show?.megaphonePodcastId));
  const state = podcastState(job);

  const retry = async () => {
    const r = await api(`/admin/podcast/jobs/${job.id}/retry`, 'POST');
    if (r?.ok) { flash('Rimesso in coda'); reload(); }
    else if (r) flash((await r.json().catch(() => ({}))).detail || 'Errore');
  };

  return (
    <div className="border rounded-lg p-3 bg-purple-50/50 border-purple-200 space-y-3">
      <div className="text-xs font-semibold text-purple-900 uppercase tracking-wide">
        Episodio podcast · questo segmento
      </div>

      {!cfg?.configured ? (
        <p className="text-xs text-gray-600">
          Megaphone non è ancora configurato: il segmento resta marcato, ma non viene pubblicato nulla.
        </p>
      ) : (
        <>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Podcast di destinazione</span>
            <select
              value={item.podcastPodcastId || ''}
              onChange={(e) => set('podcastPodcastId', e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">
                {inherited ? `— dal programma: ${inherited.title} —` : '— nessuno (scegli un podcast) —'}
              </option>
              {megaphoneShows.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Titolo episodio</span>
            <input
              value={item.podcastTitle || ''}
              onChange={(e) => set('podcastTitle', e.target.value)}
              placeholder={job?.title || 'Generato dal titolo del segmento e dalla data'}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Descrizione</span>
            <textarea
              value={item.podcastSummary || ''}
              onChange={(e) => set('podcastSummary', e.target.value)}
              placeholder={job?.summary || 'Generata da conduttore e ospiti'}
              rows={2}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
            />
          </label>
        </>
      )}

      {job && (
        <div className="flex items-center justify-between gap-2 border-t border-purple-200 pt-2">
          <div className="text-xs text-gray-600 min-w-0">
            <span className="mr-1">{state.icon}</span>{state.label}
            {job.start && <span className="text-gray-400"> · {job.start}–{job.end}</span>}
            {job.error && <div className="text-red-600 truncate" title={job.error}>{job.error}</div>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {job.megaphoneUrl && (
              <a href={job.megaphoneUrl} target="_blank" rel="noopener noreferrer"
                 className="text-xs text-[#009246] underline">apri in Megaphone</a>
            )}
            {job.status === 'failed' && (
              <button onClick={retry} className="text-xs px-2 py-1 border rounded hover:bg-white">Riprova</button>
            )}
          </div>
        </div>
      )}
      {!job && (
        <p className="text-[11px] text-gray-500">
          L'episodio viene creato dopo la messa in onda: l'audio è tagliato dal logger di Melbourne
          (che registra circa 07:00–18:00) e pubblicato su Megaphone, che lo distribuisce a Spotify.
        </p>
      )}
    </div>
  );
};

// ---------- Podcast episodes ----------
const PodcastModal = ({ jobs, cfg, api, flash, reload, onOpenDate, onClose }) => {
  const [busy, setBusy] = useState('');
  const act = async (job, what) => {
    setBusy(job.id);
    const r = await api(`/admin/podcast/jobs/${job.id}/${what}`, 'POST');
    setBusy('');
    if (r?.ok) { flash(what === 'retry' ? 'Rimesso in coda' : 'Pubblicato'); reload(); }
    else if (r) flash((await r.json().catch(() => ({}))).detail || 'Errore');
  };

  const seen = cfg?.agentLastSeen ? new Date(cfg.agentLastSeen) : null;
  const staleMinutes = seen ? Math.round((Date.now() - seen.getTime()) / 60000) : null;

  return (
    <Modal title="Podcast" onClose={onClose} wide>
      {/* Health: the two things that silently stop episodes appearing */}
      <div className="grid sm:grid-cols-2 gap-2 mb-4">
        <div className={`rounded-lg p-2 text-xs border ${cfg?.configured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="font-semibold">{cfg?.configured ? '✅ Megaphone collegato' : '⚠️ Megaphone non configurato'}</div>
          <div className="text-gray-600">Gli episodi pubblicati arrivano su Spotify automaticamente.</div>
        </div>
        <div className={`rounded-lg p-2 text-xs border ${staleMinutes !== null && staleMinutes < 30 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="font-semibold">
            {staleMinutes === null ? '⚠️ Agente mai visto'
              : staleMinutes < 30 ? `✅ Agente attivo (${staleMinutes} min fa)`
              : `⚠️ Agente non risponde da ${staleMinutes} min`}
          </div>
          <div className="text-gray-600">
            {cfg?.agentHost ? `PC: ${cfg.agentHost}` : 'Il PC in studio che taglia l\'audio dal logger.'}
          </div>
        </div>
      </div>

      {jobs.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">
          Nessun episodio per questa settimana. Marca un segmento con 🎧, oppure attiva
          "Pubblica l'intero programma" nella scheda Programmi.
        </p>
      ) : (
        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
          {jobs.map((j) => {
            const st = podcastState(j);
            return (
              <div key={j.id} className="border rounded-lg p-2 flex items-start gap-3">
                <span className="text-lg leading-none pt-0.5" title={st.label}>{st.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{j.title}</div>
                  <div className="text-xs text-gray-500">
                    <button onClick={() => onOpenDate(j.date)} className="underline hover:text-[#009246]">{j.date}</button>
                    {' · '}{j.start}–{j.end}
                    {' · '}{j.kind === 'show' ? 'programma intero' : 'segmento'}
                    {j.showName ? ` · ${j.showName}` : ''}
                  </div>
                  <div className="text-xs text-gray-400">{st.label}</div>
                  {j.error && <div className="text-xs text-red-600 break-words">{j.error}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {j.audioUrl && (
                    <a href={j.audioUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#009246] underline">audio</a>
                  )}
                  {j.megaphoneUrl && (
                    <a href={j.megaphoneUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#009246] underline">Megaphone</a>
                  )}
                  {(j.status === 'failed' || j.status === 'review') && (
                    <button disabled={busy === j.id} onClick={() => act(j, 'retry')}
                            className="text-xs px-2 py-1 border rounded hover:bg-gray-50 disabled:opacity-50">Riprova</button>
                  )}
                  {j.status === 'published' && j.draft && (
                    <button disabled={busy === j.id} onClick={() => act(j, 'publish')}
                            className="text-xs px-2 py-1 border rounded bg-[#009246] text-white disabled:opacity-50">Pubblica</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

// ---------- Shows management ----------
const ShowsModal = ({ shows, api, onClose, reload, flash, megaphoneShows = [], podcastCfg }) => {
  const [list, setList] = useState(shows);
  const [draft, setDraft] = useState({ name: '', host: '', hostEmail: '', color: '#009246' });
  const refresh = async () => { const r = await api('/admin/shows'); if (r?.ok) setList(await r.json()); };
  const add = async () => { if (!draft.name.trim()) return; const r = await api('/admin/shows', 'POST', draft); if (r?.ok) { setDraft({ name: '', host: '', hostEmail: '', color: '#009246' }); refresh(); reload(); flash('Programma aggiunto'); } };
  const saveShow = async (s, patch) => { const r = await api(`/admin/shows/${s.id}`, 'PUT', { ...s, ...patch }); if (r?.ok) { refresh(); reload(); flash('Salvato'); } };
  const del = async (id) => { if (!window.confirm('Eliminare il programma?')) return; const r = await api(`/admin/shows/${id}`, 'DELETE'); if (r?.ok) { refresh(); reload(); } };
  return (
    <Modal title="Programmi" onClose={onClose} wide>
      <p className="text-xs text-gray-500 mb-2">{list.length} programmi</p>
      <div className="space-y-2 mb-4 max-h-[45vh] overflow-y-auto pr-1">
        {list.map((s) => (
          <div key={s.id} className="flex items-center gap-2 border rounded-lg p-2">
            <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: s.color }} />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{s.name}</div>
              {s.host && <div className="text-xs text-gray-500">{s.host}</div>}
              <input
                defaultValue={s.hostEmail || ''}
                onBlur={(e) => { if (e.target.value !== (s.hostEmail || '')) saveShow(s, { hostEmail: e.target.value }); }}
                placeholder="Email conduttore (aggiunta agli inviti Teams)"
                className="mt-1 w-full px-2 py-1 border rounded text-xs"
              />
              {/* Podcast. Independent of the per-segment 🎧 flag: a programme can
                  publish a full daily episode AND split-out segments. */}
              <div className="mt-1.5 pt-1.5 border-t space-y-1.5">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={!!s.podcastWholeShow}
                    onChange={(e) => saveShow(s, { podcastWholeShow: e.target.checked })}
                  />
                  🎧 Pubblica l'intero programma come episodio giornaliero
                </label>
                {(s.podcastWholeShow || s.megaphonePodcastId) && (
                  <div className="grid grid-cols-[1fr_auto_auto] gap-1.5 items-center">
                    <select
                      value={s.megaphonePodcastId || ''}
                      onChange={(e) => saveShow(s, { megaphonePodcastId: e.target.value })}
                      className="px-2 py-1 border rounded text-xs min-w-0"
                    >
                      <option value="">
                        {podcastCfg?.configured ? '— scegli il podcast Megaphone —' : '— Megaphone non configurato —'}
                      </option>
                      {megaphoneShows.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                    </select>
                    <input
                      type="time" defaultValue={s.podcastStart || ''}
                      onBlur={(e) => { if (e.target.value !== (s.podcastStart || '')) saveShow(s, { podcastStart: e.target.value }); }}
                      title="Inizio fisso (vuoto = dal primo segmento del giorno)"
                      className="px-1.5 py-1 border rounded text-xs"
                    />
                    <input
                      type="time" defaultValue={s.podcastEnd || ''}
                      onBlur={(e) => { if (e.target.value !== (s.podcastEnd || '')) saveShow(s, { podcastEnd: e.target.value }); }}
                      title="Fine fissa (vuoto = fino all'ultimo segmento del giorno)"
                      className="px-1.5 py-1 border rounded text-xs"
                    />
                  </div>
                )}
                {(s.podcastWholeShow || s.megaphonePodcastId) && (
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!s.podcastDraft}
                      onChange={(e) => saveShow(s, { podcastDraft: e.target.checked })}
                    />
                    <span>📝 Pubblica come <b>bozza</b> da approvare (non va su Spotify finché non premi Pubblica)</span>
                  </label>
                )}
                {(s.podcastWholeShow || s.megaphonePodcastId) && (
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="whitespace-nowrap">Pubblica dal</span>
                    <input
                      type="date" defaultValue={s.podcastFrom || ''}
                      onBlur={(e) => { if (e.target.value !== (s.podcastFrom || '')) saveShow(s, { podcastFrom: e.target.value }); }}
                      title="Niente prima di questa data viene pubblicato. Impostata automaticamente a oggi quando attivi il podcast, così le puntate passate (che spesso esistono già su Megaphone) non vengono ripubblicate."
                      className="px-1.5 py-1 border rounded text-xs"
                    />
                  </label>
                )}
                {(s.podcastWholeShow || s.megaphonePodcastId) && (
                  <input
                    defaultValue={s.podcastTitleTemplate || ''}
                    onBlur={(e) => { if (e.target.value !== (s.podcastTitleTemplate || '')) saveShow(s, { podcastTitleTemplate: e.target.value }); }}
                    placeholder="Titolo episodio: {show} {date} — es. {date} per «07-08-26»"
                    title="Segnaposto: {show} {date} {date_long} {title} {guests}. Vuoto = «Nome programma 07-08-26»."
                    className="w-full px-2 py-1 border rounded text-xs"
                  />
                )}
              </div>
            </div>
            <button onClick={() => del(s.id)} className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Nessun programma. Aggiungine uno sotto.</p>}
      </div>
      <div className="border-t pt-3 grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Nome programma" className="px-2 py-1.5 border rounded" />
        <input value={draft.host} onChange={(e) => setDraft({ ...draft, host: e.target.value })} placeholder="Conduttore" className="px-2 py-1.5 border rounded" />
        <input type="color" value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} className="w-10 h-9 border rounded" />
        <Button onClick={add} className="bg-[#009246]"><Plus className="w-4 h-4" /></Button>
      </div>
    </Modal>
  );
};

// ---------- Users management (admin) ----------
const UsersModal = ({ users, shows, api, onClose, reload, flash }) => {
  const [list, setList] = useState(users);
  const [draft, setDraft] = useState({ username: '', password: '', role: 'host', shows: [] });
  const [showSearch, setShowSearch] = useState('');
  const refresh = async () => { const r = await api('/admin/users'); if (r?.ok) setList(await r.json()); };
  const toggleShow = (id) => setDraft((d) => ({ ...d, shows: d.shows.includes(id) ? d.shows.filter((x) => x !== id) : [...d.shows, id] }));
  const add = async () => {
    if (!draft.username.trim() || !draft.password) { flash('Username e password obbligatori'); return; }
    if (draft.role === 'host' && !draft.shows.length) { flash('Seleziona almeno un programma per un host'); return; }
    const r = await api('/admin/users', 'POST', draft);
    if (r?.ok) { setDraft({ username: '', password: '', role: 'host', shows: [] }); setShowSearch(''); refresh(); flash('Utente creato'); }
    else if (r) flash('Errore (username già esistente?)');
  };
  const del = async (u) => { if (!window.confirm(`Eliminare ${u}?`)) return; const r = await api(`/admin/users/${u}`, 'DELETE'); if (r?.ok) refresh(); };

  const filteredShows = shows.filter((s) => s.name.toLowerCase().includes(showSearch.toLowerCase()));
  const nameOf = (id) => shows.find((s) => s.id === id)?.name;

  return (
    <Modal title="Utenti" onClose={onClose} wide>
      {/* Existing users */}
      <div className="mb-5">
        <h4 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Utenti esistenti</h4>
        {list.length === 0 ? (
          <p className="text-sm text-gray-400 border border-dashed rounded-lg p-4 text-center">
            Nessun utente ancora. Crea il primo qui sotto.<br />
            <span className="text-xs">(gli accessi <b>admin</b> e <b>palinsesto</b> di sistema restano sempre attivi)</span>
          </p>
        ) : (
          <div className="space-y-2">
            {list.map((u) => (
              <div key={u.username} className="flex items-start gap-2 border rounded-lg p-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{u.username}
                    <span className="ml-2 text-[10px] uppercase bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{u.role}</span>
                  </div>
                  {u.role === 'host' && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {(u.shows || []).map(nameOf).filter(Boolean).join(', ') || '(nessun programma assegnato)'}
                    </div>
                  )}
                </div>
                <button onClick={() => del(u.username)} className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create user */}
      <div className="border-t pt-4 space-y-3">
        <h4 className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Nuovo utente</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block"><span className="text-sm font-medium">Username</span>
            <input value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })} placeholder="es. tony" className="w-full mt-1 px-3 py-2 border rounded-lg" /></label>
          <label className="block"><span className="text-sm font-medium">Password</span>
            <input type="text" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} placeholder="password iniziale" className="w-full mt-1 px-3 py-2 border rounded-lg" /></label>
        </div>
        <label className="block"><span className="text-sm font-medium">Ruolo</span>
          <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded-lg">
            <option value="host">Host — può modificare solo i programmi assegnati</option>
            <option value="producer">Producer — può modificare tutta la scaletta</option>
            <option value="admin">Admin — tutto, inclusa la gestione utenti</option>
          </select></label>

        {draft.role === 'host' && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">Programmi assegnati</span>
              <span className="text-xs text-gray-500">{draft.shows.length} selezionati</span>
            </div>
            {draft.shows.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {draft.shows.map((id) => (
                  <span key={id} className="text-xs bg-[#009246] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                    {nameOf(id)}
                    <button onClick={() => toggleShow(id)} className="hover:opacity-70">×</button>
                  </span>
                ))}
              </div>
            )}
            <input
              value={showSearch}
              onChange={(e) => setShowSearch(e.target.value)}
              placeholder="Cerca un programma…"
              className="w-full px-3 py-2 border rounded-lg text-sm mb-1"
            />
            <div className="border rounded-lg max-h-44 overflow-y-auto divide-y">
              {filteredShows.length === 0 && <p className="text-xs text-gray-400 p-3 text-center">Nessun programma trovato</p>}
              {filteredShows.map((s) => (
                <label key={s.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={draft.shows.includes(s.id)} onChange={() => toggleShow(s.id)} />
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                  <span className="flex-1 truncate">{s.name}</span>
                  {s.host && <span className="text-xs text-gray-400 truncate">{s.host}</span>}
                </label>
              ))}
            </div>
          </div>
        )}
        <Button onClick={add} className="bg-[#009246] w-full"><Plus className="w-4 h-4 mr-1" />Crea utente</Button>
      </div>
    </Modal>
  );
};

// ---------- Share link ----------
const ShareModal = ({ token, api, setToken, onClose, flash }) => {
  const url = `${window.location.origin}/scaletta/shared/${token}`;
  const rotate = async () => { if (!window.confirm('Rigenerare il link? Quello vecchio smetterà di funzionare.')) return; const r = await api('/admin/share-link/rotate', 'POST'); if (r?.ok) { setToken((await r.json()).token); flash('Nuovo link generato'); } };
  return (
    <Modal title="Link di sola lettura" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-3">Chiunque abbia questo link può <b>vedere</b> la scaletta (senza i contatti degli ospiti) e non può modificarla.</p>
      <div className="flex gap-2">
        <input readOnly value={url} className="flex-1 px-3 py-2 border rounded-lg text-sm bg-gray-50" />
        <Button onClick={() => { navigator.clipboard?.writeText(url); flash('Copiato'); }} variant="outline"><Copy className="w-4 h-4" /></Button>
      </div>
      <button onClick={rotate} className="text-xs text-red-500 mt-3 hover:underline flex items-center gap-1"><Settings className="w-3 h-3" />Rigenera link</button>
    </Modal>
  );
};

// ---------- Search across every week ----------
const SearchModal = ({ api, shows, onOpenDate, onClose }) => {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async (term) => {
    if (term.trim().length < 2) { setRes(null); return; }
    setBusy(true);
    const r = await api(`/admin/rundown/search?q=${encodeURIComponent(term.trim())}`);
    if (r?.ok) setRes(await r.json());
    setBusy(false);
  }, [api]);

  useEffect(() => { const t = setTimeout(() => run(q), 350); return () => clearTimeout(t); }, [q, run]);

  return (
    <Modal title="Cerca nella scaletta" onClose={onClose} wide>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Nome ospite, programma, conduttore, titolo, note…"
        className="w-full px-3 py-2 border rounded-lg mb-3"
      />
      {q.trim().length < 2 && <p className="text-xs text-gray-400">Digita almeno 2 caratteri. Cerca in tutte le settimane.</p>}
      {busy && <p className="text-sm text-gray-400">Ricerca…</p>}
      {res && !busy && (
        <>
          <p className="text-xs text-gray-500 mb-2">{res.count} risultati{res.count > res.items.length ? ` (primi ${res.items.length})` : ''}</p>
          <div className="divide-y max-h-[55vh] overflow-y-auto">
            {res.items.map((it) => {
              const s = shows.find((x) => x.id === it.showId);
              return (
                <button key={it.id} onClick={() => onOpenDate(it.date)} className="w-full text-left py-2 px-1 hover:bg-gray-50 flex gap-3">
                  <div className="w-24 flex-shrink-0 text-xs text-gray-500">
                    <div className="font-medium text-gray-700">{new Date(`${it.date}T12:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: '2-digit' })}</div>
                    <div className="tabular-nums">{it.time}</div>
                  </div>
                  <div className="min-w-0 flex-1" style={{ borderLeft: `3px solid ${s?.color || '#e5e7eb'}`, paddingLeft: 8 }}>
                    <div className="font-medium text-sm truncate">{it.title}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {it.showName || s?.name}{(it.showName || s?.name) && it.host ? ' · ' : ''}{it.host}
                    </div>
                    {it.guests?.length > 0 && (
                      <div className="text-xs text-gray-400 truncate">👤 {it.guests.map((g) => g.name).filter(Boolean).join(', ')}</div>
                    )}
                  </div>
                </button>
              );
            })}
            {res.items.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">Nessun risultato.</p>}
          </div>
        </>
      )}
    </Modal>
  );
};

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className={`bg-white rounded-xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[92vh] overflow-y-auto`}>
      <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white"><h3 className="text-lg font-bold">{title}</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

export default AdminScaletta;
